import type React from "react";
import { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Radio,
  FormControl,
  Select,
  MenuItem,
  Paper,
  Divider,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import type { Assessment, Question } from "../types/course.types";

interface AssessmentsTabProps {
  assessments: Assessment[];
  onAssessmentsChange: (assessments: Assessment[]) => void;
}

const AssessmentsTab: React.FC<AssessmentsTabProps> = ({
  assessments,
  onAssessmentsChange,
}) => {
  const [currentAssessment, setCurrentAssessment] = useState<Assessment>({
    id: Date.now().toString(),
    title: "New Assessment",
    passMark: 60,
    attemptsAllowed: 2,
    timeLimit: undefined,
    status: "draft",
    questions: [
      {
        id: Date.now().toString(),
        questionText: "",
        answers: [
          { id: `${Date.now()}-1`, text: "", isCorrect: false },
          { id: `${Date.now()}-2`, text: "", isCorrect: false },
        ],
        explanation: "",
      },
    ],
  });

  const handleAssessmentChange = (field: keyof Assessment, value: any) => {
    setCurrentAssessment((prev) => ({ ...prev, [field]: value }));
  };

  const handleQuestionChange = (
    questionIndex: number,
    field: keyof Question,
    value: any
  ) => {
    const updatedQuestions = [...currentAssessment.questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      [field]: value,
    };
    setCurrentAssessment((prev) => ({ ...prev, questions: updatedQuestions }));
  };

  const handleAnswerChange = (
    questionIndex: number,
    answerIndex: number,
    text: string
  ) => {
    const updatedQuestions = [...currentAssessment.questions];
    updatedQuestions[questionIndex].answers[answerIndex].text = text;
    setCurrentAssessment((prev) => ({ ...prev, questions: updatedQuestions }));
  };

  const handleCorrectAnswerChange = (
    questionIndex: number,
    answerIndex: number
  ) => {
    const updatedQuestions = [...currentAssessment.questions];
    updatedQuestions[questionIndex].answers = updatedQuestions[
      questionIndex
    ].answers.map((answer, idx) => ({
      ...answer,
      isCorrect: idx === answerIndex,
    }));
    setCurrentAssessment((prev) => ({ ...prev, questions: updatedQuestions }));
  };

  const addAnswer = (questionIndex: number) => {
    const updatedQuestions = [...currentAssessment.questions];
    updatedQuestions[questionIndex].answers.push({
      id: `${Date.now()}-${updatedQuestions[questionIndex].answers.length}`,
      text: "",
      isCorrect: false,
    });
    setCurrentAssessment((prev) => ({ ...prev, questions: updatedQuestions }));
  };

  const removeAnswer = (questionIndex: number, answerIndex: number) => {
    const updatedQuestions = [...currentAssessment.questions];
    if (updatedQuestions[questionIndex].answers.length > 2) {
      updatedQuestions[questionIndex].answers.splice(answerIndex, 1);
      setCurrentAssessment((prev) => ({
        ...prev,
        questions: updatedQuestions,
      }));
    }
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: Date.now().toString(),
      questionText: "",
      answers: [
        { id: `${Date.now()}-1`, text: "", isCorrect: false },
        { id: `${Date.now()}-2`, text: "", isCorrect: false },
      ],
      explanation: "",
    };
    setCurrentAssessment((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const removeQuestion = (questionIndex: number) => {
    if (currentAssessment.questions.length > 1) {
      const updatedQuestions = currentAssessment.questions.filter(
        (_, idx) => idx !== questionIndex
      );
      setCurrentAssessment((prev) => ({
        ...prev,
        questions: updatedQuestions,
      }));
    }
  };

  const handleCreateAssessment = () => {
    onAssessmentsChange([...assessments, currentAssessment]);
    // Reset form
    setCurrentAssessment({
      id: Date.now().toString(),
      title: "New Assessment",
      passMark: 60,
      attemptsAllowed: 2,
      timeLimit: undefined,
      status: "draft",
      questions: [
        {
          id: Date.now().toString(),
          questionText: "",
          answers: [
            { id: `${Date.now()}-1`, text: "", isCorrect: false },
            { id: `${Date.now()}-2`, text: "", isCorrect: false },
          ],
          explanation: "",
        },
      ],
    });
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      {/* Assessment Settings */}
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Assessment Settings
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Assessment Title
        </Typography>
        <TextField
          fullWidth
          value={currentAssessment.title}
          onChange={(e) => handleAssessmentChange("title", e.target.value)}
          placeholder="New Assessment"
          variant="outlined"
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Pass Mark (%)
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={currentAssessment.passMark}
              onChange={(e) =>
                handleAssessmentChange("passMark", Number(e.target.value))
              }
              variant="outlined"
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Attempts Allowed
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={currentAssessment.attemptsAllowed}
              onChange={(e) =>
                handleAssessmentChange(
                  "attemptsAllowed",
                  Number(e.target.value)
                )
              }
              variant="outlined"
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Time Limit (min)
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={currentAssessment.timeLimit || ""}
              onChange={(e) =>
                handleAssessmentChange(
                  "timeLimit",
                  e.target.value ? Number(e.target.value) : undefined
                )
              }
              placeholder="Optional"
              variant="outlined"
            />
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
            Status
          </Typography>
          <FormControl fullWidth>
            <Select
              value={currentAssessment.status}
              onChange={(e) => handleAssessmentChange("status", e.target.value)}
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Questions Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Questions ({currentAssessment.questions.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addQuestion}
          sx={{ color: "white", textTransform: "none" }}
        >
          Add Question
        </Button>
      </Box>

      {currentAssessment.questions.map((question, questionIndex) => (
        <Paper
          key={question.id}
          sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0", boxShadow: 0 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Question {questionIndex + 1}
            </Typography>
            {currentAssessment.questions.length > 1 && (
              <IconButton
                onClick={() => removeQuestion(questionIndex)}
                color="error"
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <TextField
            fullWidth
            multiline
            rows={2}
            value={question.questionText}
            onChange={(e) =>
              handleQuestionChange(
                questionIndex,
                "questionText",
                e.target.value
              )
            }
            placeholder="Enter your question..."
            variant="outlined"
            sx={{ mb: 3 }}
          />

          {/* Answers */}
          <Box sx={{ mb: 2 }}>
            {question.answers.map((answer, answerIndex) => (
              <Box
                key={answer.id}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Radio
                  checked={answer.isCorrect}
                  onChange={() =>
                    handleCorrectAnswerChange(questionIndex, answerIndex)
                  }
                  size="small"
                />
                <TextField
                  fullWidth
                  value={answer.text}
                  onChange={(e) =>
                    handleAnswerChange(
                      questionIndex,
                      answerIndex,
                      e.target.value
                    )
                  }
                  placeholder={`Answer ${answerIndex + 1}`}
                  variant="outlined"
                  size="small"
                />
                {question.answers.length > 2 && (
                  <IconButton
                    onClick={() => removeAnswer(questionIndex, answerIndex)}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          <Button
            startIcon={<AddIcon />}
            onClick={() => addAnswer(questionIndex)}
            sx={{ textTransform: "none", color: "primary.main", mb: 2 }}
          >
            Add Answer
          </Button>

          {/* Explanation */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Explanation (Optional)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={question.explanation}
              onChange={(e) =>
                handleQuestionChange(
                  questionIndex,
                  "explanation",
                  e.target.value
                )
              }
              placeholder="Explain why this answer is correct..."
              variant="outlined"
            />
          </Box>
        </Paper>
      ))}

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
        <Button
          variant="contained"
          onClick={handleCreateAssessment}
          sx={{ color: "white", textTransform: "none", px: 4 }}
        >
          Create Assessment
        </Button>
        <Button variant="outlined" sx={{ textTransform: "none", px: 4 }}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default AssessmentsTab;
